import { defineStore } from 'pinia';
import axios from 'axios';
import numeral from 'numeral';

function ensureArrayBucket(map, key) {
    if (!map[key] || !Array.isArray(map[key])) {
        map[key] = [];
    }
    return map[key];
}

function resolveFuturesMarketKey(entity = {}, payload = {}) {
    return entity?.market_name
        || entity?.symbol
        || entity?.market
        || payload?.market_name
        || payload?.market
        || payload?.symbol
        || null;
}

function upsertById(list, order, { prepend = false } = {}) {
    const idx = list.findIndex(o => o?.id === order?.id);
    if (idx > -1) {
        // Preserve existing fields if event payload is partial
        list[idx] = { ...list[idx], ...order };
        return { inserted: false, updated: true };
    }
    if (prepend) list.unshift(order);
    else list.push(order);
    return { inserted: true, updated: false };
}

function removeById(list, id) {
    const idx = list.findIndex(o => o?.id === id);
    if (idx > -1) {
        list.splice(idx, 1);
        return true;
    }
    return false;
}

function bumpMetaTotal(metaMap, key, delta) {
    if (!metaMap || !key) return;
    const meta = metaMap[key];
    if (!meta || meta.total == null) return;
    const next = (parseInt(meta.total, 10) || 0) + delta;
    meta.total = Math.max(0, next);
}

export const useOrdersStore = defineStore('orders', {
    state: () => ({
        asks: {},
        bids: {},
        openOrders: {},
        openOrdersMeta: {},
        otherOpenOrders: {},
        otherOpenOrdersMeta: {},
        openOrdersTick: 0,
        // Futures: positions (legacy) and open futures orders (new engine)
        openFuturesOrders: {},
        openFuturesLimitOrders: {},
        openOptionsOrders: {},
        myOrders: {},
        /** Client clock offset in seconds (server_ts - client_ts at receive) for options remaining countdown. */
        optionsServerTimeOffset: null
    }),

    getters: {
        getOrderbook: (state) => (market, type) => {
            if (type === "bids") {
                return state.bids[market] || [];
            }
            return state.asks[market] || [];
        },
        
        getOpenOrders: (state) => (market) => {
            return state.openOrders?.[market] || [];
        },

        getOpenOrdersMeta: (state) => (market) => {
            return state.openOrdersMeta?.[market] || {};
        },

        getOtherOpenOrders: (state) => (market) => {
            return state.otherOpenOrders?.[market] || [];
        },

        getOtherOpenOrdersMeta: (state) => (market) => {
            return state.otherOpenOrdersMeta?.[market] || {};
        },
        
        getFuturesOpenOrders: (state) => (market) => {
            return state.openFuturesOrders?.[market] || [];
        },

        getFuturesOpenLimitOrders: (state) => (market) => {
            return state.openFuturesLimitOrders?.[market] || [];
        },
        
        getOptionsOpenOrders: (state) => (market) => {
            return state.openOptionsOrders?.[market] || [];
        },

        getOptionsServerTimeOffset: (state) => state.optionsServerTimeOffset
    },

    actions: {
        async fetchOrders({ market, route }) {
            try {
                const response = await axios.get(route, {
                    params: { market }
                });
                const orderbook = response.data;
                this.initOrderbook({ bids: orderbook.bids, asks: orderbook.asks, market });
            } catch (error) {
                console.error('Failed to fetch orders:', error);
            }
        },

        initOrderbook({ bids, asks, market }) {
            if (bids) {
                this.bids[market] = bids;
            }
            if (asks) {
                this.asks[market] = asks;
            }
        },

        liquidityOrders({ orders, market }) {
            this.bids[market] = orders['bids'];
            this.asks[market] = orders['asks'];
        },

        async fetchOpenOrders({ market, route, params = {} }) {
            try {
                const response = await axios.get(route, {
                    params: { market, ...params }
                });
                const payload = response.data;

                // New (pro) API response shape
                if (payload?.this_market && payload?.other_markets) {
                    this.openOrders[market] = payload.this_market.data || [];
                    this.openOrdersMeta[market] = payload.this_market.meta || {};

                    this.otherOpenOrders[market] = payload.other_markets.data || [];
                    this.otherOpenOrdersMeta[market] = payload.other_markets.meta || {};
                } else {
                    // Legacy fallback
                    this.openOrders[market] = response.data.data;
                }
            } catch (error) {
                console.error('Failed to fetch open orders:', error);
            }
        },

        async cancelOpenOrdersBatch({ route, payload }) {
            return axios.post(route, payload);
        },

        async fetchOptionsOpenOrders({ market, route }) {
            try {
                const response = await axios.get(route, {
                    params: { market }
                });
                const data = response.data?.data ?? response.data;
                this.openOptionsOrders[market] = Array.isArray(data) ? data : [];
                const serverTs = response.data?.server_ts;
                if (typeof serverTs === 'number') {
                    this.optionsServerTimeOffset = serverTs - (Date.now() / 1000);
                }
            } catch (error) {
                console.error('Failed to fetch options open orders:', error);
            }
        },

        /** Called by PrivateChannel when OptionsStatusUpdated is received (option settled). */
        removeOptionFromOpenOrders(market, optionId) {
            if (!market || !optionId) return;
            const list = this.openOptionsOrders[market];
            if (!Array.isArray(list)) return;
            const idx = list.findIndex((o) => o.id === optionId);
            if (idx > -1) {
                list.splice(idx, 1);
            }
        },

        /** Called by PrivateChannel when OptionOrderPlaced is received (new option created). */
        addOptionToOpenOrders(market, orderPayload) {
            if (!market || !orderPayload) return;
            if (!this.openOptionsOrders[market]) {
                this.openOptionsOrders[market] = [];
            }
            const list = this.openOptionsOrders[market];
            const exists = list.some((o) => o.id === orderPayload.id);
            if (!exists) {
                list.unshift(orderPayload);
            }
            const serverTs = orderPayload?.server_ts;
            if (typeof serverTs === 'number') {
                this.optionsServerTimeOffset = serverTs - (Date.now() / 1000);
            }
        },

        async fetchFuturesOpenOrders({ market, route }) {
            try {
                const response = await axios.get(route, {
                    params: { market }
                });
                this.openFuturesOrders[market] = response.data.data;
            } catch (error) {
                console.error('Failed to fetch futures open orders:', error);
            }
        },

        updateOrderbook({ order, type, market }) {
            if (type === "store") {
                this.storeOrder({ order, market });
            } else if (type === "update") {
                this.updateOrder({ order, market });
            } else if (type === "cancel") {
                this.cancelOrder({ order, market });
            }
        },

        storeOrder({ order, market }) {
            // Skip if quantity is 0
            if (order.quantity == 0) return;

            // Store order in market orderbook
            const orders = order.side === "buy" ? this.bids[market] : this.asks[market];
            if (!orders) return;

            const findOrder = orders.findIndex(x => String(x.price) === String(order.price));

            if (findOrder > -1) {
                const initialQuantity = orders[findOrder].quantity;
                orders.splice(findOrder, 1);
                orders.push({
                    price: order.price,
                    quantity: numeral(initialQuantity).add(numeral(order.quantity).value()).value()
                });
            } else {
                orders.push({
                    price: order.price,
                    quantity: order.quantity,
                });
            }
        },

        updateOrder({ order, market }) {
            const orders = order.side === "buy" ? this.bids[market] : this.asks[market];
            if (!orders) return;

            const findOrder = orders.findIndex(x => String(x.price) === String(order.price));

            if (findOrder > -1) {
                const initialQuantity = orders[findOrder].quantity;
                orders.splice(findOrder, 1);
                const updatedQuantity = numeral(initialQuantity).subtract(numeral(order.updated_quantity).value()).value();

                if (updatedQuantity > 0) {
                    orders.push({
                        price: order.price,
                        quantity: updatedQuantity,
                    });
                }
            }

            // Update from open orders
            const openOrders = this.openOrders[market];
            if (openOrders) {
                const findUpdatedOrder = openOrders.findIndex(x => x.id === order.id);
                if (findUpdatedOrder > -1) {
                    if (order.quantity > 0) {
                        openOrders[findUpdatedOrder] = order;
                    } else {
                        openOrders.splice(findUpdatedOrder, 1);
                    }
                }
            }
        },

        cancelOrder({ order, market }) {
            // Remove from orderbook
            const orders = order.side === "buy" ? this.bids[order.market] : this.asks[order.market];
            if (orders) {
                const findOrder = orders.findIndex(x => String(x.price) === String(order.price));

                if (findOrder > -1) {
                    const initialQuantity = orders[findOrder].quantity;
                    orders.splice(findOrder, 1);
                    const updatedQuantity = numeral(initialQuantity).subtract(numeral(order.quantity).value()).value();

                    if (updatedQuantity > 0 && order.quantity !== 0) {
                        orders.push({
                            price: order.price,
                            quantity: updatedQuantity,
                        });
                    }
                }
            }

            // Remove from open orders
            const openOrders = this.openOrders[order.market];
            if (openOrders) {
                const findCancelledOrder = openOrders.findIndex(x => x.id === order.id);
                if (findCancelledOrder > -1) {
                    openOrders.splice(findCancelledOrder, 1);
                }
            }
        },

        // Handle OrderCreated event
        handleOrderCreated(payload) {
            const order = payload?.order || {};
            const market = order.market || payload?.market;
            if (!order?.id || !market) return;
            order.market = market;

            // This market list (if user is on that market tab somewhere)
            const marketList = ensureArrayBucket(this.openOrders, market);
            const { inserted } = upsertById(marketList, order, { prepend: true });
            if (inserted) bumpMetaTotal(this.openOrdersMeta, market, +1);

            // Other markets lists: update any loaded contexts
            for (const contextMarket of Object.keys(this.otherOpenOrders || {})) {
                const ctxList = ensureArrayBucket(this.otherOpenOrders, contextMarket);
                if (contextMarket === market) {
                    const removed = removeById(ctxList, order.id);
                    if (removed) bumpMetaTotal(this.otherOpenOrdersMeta, contextMarket, -1);
                    continue;
                }
                const { inserted: ins2 } = upsertById(ctxList, order, { prepend: true });
                if (ins2) bumpMetaTotal(this.otherOpenOrdersMeta, contextMarket, +1);
            }
            this.openOrdersTick++;
        },

        // Handle OrderCancelled event
        handleOrderCancelled(payload) {
            const order = payload?.order || {};
            const market = order.market || payload?.market;
            const id = order?.id;
            if (!id) return;

            // Remove from all "this market" buckets (defensive)
            for (const m of Object.keys(this.openOrders || {})) {
                const list = ensureArrayBucket(this.openOrders, m);
                const removed = removeById(list, id);
                if (removed) bumpMetaTotal(this.openOrdersMeta, m, -1);
            }

            // Remove from all "other markets" context buckets
            for (const ctx of Object.keys(this.otherOpenOrders || {})) {
                const list = ensureArrayBucket(this.otherOpenOrders, ctx);
                const removed = removeById(list, id);
                if (removed) bumpMetaTotal(this.otherOpenOrdersMeta, ctx, -1);
            }

            // Also ensure it doesn't hang around if we know its market
            if (market && this.otherOpenOrders?.[market]) {
                const list = ensureArrayBucket(this.otherOpenOrders, market);
                const removed = removeById(list, id);
                if (removed) bumpMetaTotal(this.otherOpenOrdersMeta, market, -1);
            }
            this.openOrdersTick++;
        },

        // Handle OrderFilled event
        handleOrderFilled(payload) {
            const order = payload?.order || {};
            const id = order?.id;
            if (!id) return;

            // Filled => should disappear from open lists
            for (const m of Object.keys(this.openOrders || {})) {
                const list = ensureArrayBucket(this.openOrders, m);
                const removed = removeById(list, id);
                if (removed) bumpMetaTotal(this.openOrdersMeta, m, -1);
            }
            for (const ctx of Object.keys(this.otherOpenOrders || {})) {
                const list = ensureArrayBucket(this.otherOpenOrders, ctx);
                const removed = removeById(list, id);
                if (removed) bumpMetaTotal(this.otherOpenOrdersMeta, ctx, -1);
            }
            this.openOrdersTick++;
        },

        // Handle OrderPartiallyFilled event
        handleOrderPartiallyFilled(payload) {
            const order = payload?.order || {};
            const market = order.market || payload?.market;
            if (!order?.id || !market) return;
            order.market = market;

            // Update/Upsert in any loaded buckets (prevents duplicates + stale rows)
            const marketList = ensureArrayBucket(this.openOrders, market);
            const { inserted } = upsertById(marketList, order);
            if (inserted) bumpMetaTotal(this.openOrdersMeta, market, +1);

            for (const ctx of Object.keys(this.otherOpenOrders || {})) {
                const list = ensureArrayBucket(this.otherOpenOrders, ctx);
                if (ctx === market) {
                    const removed = removeById(list, order.id);
                    if (removed) bumpMetaTotal(this.otherOpenOrdersMeta, ctx, -1);
                    continue;
                }
                const { inserted: ins2 } = upsertById(list, order);
                if (ins2) bumpMetaTotal(this.otherOpenOrdersMeta, ctx, +1);
            }
            this.openOrdersTick++;
        },

        // Handle OrderUpdated event
        handleOrderUpdated(payload) {
            const order = payload?.order || {};
            const market = order.market || payload?.market;
            if (!order?.id || !market) return;
            order.market = market;

            const marketList = ensureArrayBucket(this.openOrders, market);
            const { inserted } = upsertById(marketList, order);
            if (inserted) bumpMetaTotal(this.openOrdersMeta, market, +1);

            for (const ctx of Object.keys(this.otherOpenOrders || {})) {
                const list = ensureArrayBucket(this.otherOpenOrders, ctx);
                if (ctx === market) {
                    const removed = removeById(list, order.id);
                    if (removed) bumpMetaTotal(this.otherOpenOrdersMeta, ctx, -1);
                    continue;
                }
                const { inserted: ins2 } = upsertById(list, order);
                if (ins2) bumpMetaTotal(this.otherOpenOrdersMeta, ctx, +1);
            }
            this.openOrdersTick++;
        },

        // Handle FuturesPositionUpdated event (positions are treated as "open futures orders")
        handleFuturesPositionUpdated(payload) {
            const position = payload?.position || payload?.order || {};
            const market = resolveFuturesMarketKey(position, payload);
            const id = position?.id;
            if (!id || !market) return;

            const list = ensureArrayBucket(this.openFuturesOrders, market);

            // Only keep open positions in open list
            if (position.status && position.status !== 'open') {
                removeById(list, id);
                return;
            }

            upsertById(list, position, { prepend: true });
        },

        // ---- Futures order lifecycle (new matching engine) ----
        handleFuturesOrderCreated(payload) {
            const order = payload?.order || {};
            const market = resolveFuturesMarketKey(order, payload);
            const id = order?.id;
            if (!id || !market) return;

            // Market orders execute immediately; they never rest on the book
            if (order.type === 'market') return;

            const list = ensureArrayBucket(this.openFuturesLimitOrders, market);
            upsertById(list, order, { prepend: true });
        },

        handleFuturesOrderUpdated(payload) {
            const order = payload?.order || {};
            const market = resolveFuturesMarketKey(order, payload);
            const id = order?.id;
            if (!id || !market) return;
            const list = ensureArrayBucket(this.openFuturesLimitOrders, market);
            // Remove if no longer open
            if (order.status && order.status !== 'open' && order.status !== 'partially_filled') {
                removeById(list, id);
                return;
            }
            upsertById(list, order);
        },

        handleFuturesOrderPartiallyFilled(payload) {
            return this.handleFuturesOrderUpdated(payload);
        },

        handleFuturesOrderFilled(payload) {
            const order = payload?.order || {};
            const id = order?.id;
            if (!id) return;
            for (const m of Object.keys(this.openFuturesLimitOrders || {})) {
                const list = ensureArrayBucket(this.openFuturesLimitOrders, m);
                removeById(list, id);
            }
        },

        handleFuturesOrderCancelled(payload) {
            return this.handleFuturesOrderFilled(payload);
        },

        // Debug method
        debugOrders() {
        }
    }
});



